// src/pages/BeingsListPage.tsx
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { Box, useTheme } from '@mui/material';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Add as AddIcon, Person as PersonAddIcon } from '@mui/icons-material';
import {
  Client,
  clientsAPI,
  petsAPI,
  PaginatedResult,
  referenceDataAPI,
  PetType,
} from '../services/api';
import AlphabeticalFilter from '../components/AlphabeticalFilter';
import PageNumberText from '../components/common/PageNumberText';
import PaginationBottom from '../components/common/PaginationBottom';
import ClientsList from '../components/lists/BeingsList/ClientsList';
import PetsList from '../components/lists/BeingsList/PetsList';
import { ExtendedPet } from '../components/lists/BeingsList/beingsListTypes';
import { FloatingAccessButton } from '../components/common/FloatingAccessButton';
import { useErrorHandler } from '../errors';
import { PageError } from '../components/common/PageError';
import { getApiErrorMessage } from '../utils/validationHelpers';
import LoadingSpinner from '../components/common/LoadingSpinner';
import VisuallyHiddenHeading from '../components/common/VisuallyHiddenHeading';
import { useBeingsFilter, FilterState } from '../hooks/useBeingsFilter';
import { FilterDrawer } from '../components/lists/BeingsList/BeingsFilterDrawer';
import { colors } from '../theme/theme';

const PageContainer = styled(Box)(() => ({
  width: '100%',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
}));

const ListContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '0',
  width: '100%',
  margin: 0,
  padding: 0,
});

const BottomRow = styled(Box)(({ theme }) => ({
  position: 'sticky',
  bottom: 0,
  backgroundColor: theme.palette.tertiary.main,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 30,
  boxShadow: colors.shadows.powerUp,
}));

const OnePageBottomText = styled(Box)(() => ({
  marginTop: '1rem',
  fontSize: '2rem',
}));

const BeingsListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [searchParams] = useSearchParams();

  const { handleError } = useErrorHandler();

  const beingType = location.pathname.includes('/pets') ? 'pets' : 'clients';

  const [items, setItems] = useState<(Client | ExtendedPet)[]>([]);
  const [allPetsForSiblingCalc, setAllPetsForSiblingCalc] = useState<
    ExtendedPet[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);

  const {
    filterState,
    setFilter,
    commitFilters,
    clearFilters,
    activeFilterCount,
    getPaginationParams,
  } = useBeingsFilter(beingType as 'clients' | 'pets');

  const [petTypes, setPetTypes] = useState<PetType[]>([]);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [showMobileAlphabetMenu, setShowMobileAlphabetMenu] = useState(false);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const [localTextFilter, setLocalTextFilter] = useState(
    filterState.searchText || ''
  );

  // Open-row state seeded from URL ?open=N. Shared with ClientsList/PetsList in
  // controlled mode so the page persists this to the URL as a navigation-
  // restorable marker.
  const [openItemId, setOpenItemIdState] = useState<number | null>(() => {
    const v = searchParams.get('open');
    return v ? parseInt(v) : null;
  });

  // One-shot: send openId to the API on the first load if URL had ?open=N,
  // so the server returns the page containing that item.
  const pendingOpenId = useRef<number | null>(openItemId);
  // Suppress the next loadData effect run after we adopt the server's
  // pageNumber from an auto-jump (we already have the data).
  const skipNextLoad = useRef(false);
  // Flag set when the auto-jump load lands successfully, telling a follow-up
  // effect to scroll the open row into view once the list has rendered.
  const shouldScrollToOpen = useRef(false);

  // For the open-row marker we bypass react-router's setSearchParams and
  // write directly to history. setSearchParams triggers a location change
  // that ScrollResetWrapper observes, which would scroll the page to the
  // top each time a row opens. replaceState updates the URL silently so
  // back-navigation still restores the marker but no location-change
  // listener fires.
  const replaceUrlParam = useCallback((key: string, value?: string) => {
    const url = new URL(window.location.href);
    if (value) {
      url.searchParams.set(key, value);
    } else {
      url.searchParams.delete(key);
    }
    window.history.replaceState(window.history.state, '', url.toString());
  }, []);

  const setOpenItemId = useCallback(
    (id: number | null) => {
      setOpenItemIdState(id);
      replaceUrlParam('open', id === null ? undefined : String(id));
    },
    [replaceUrlParam]
  );

  useEffect(() => {
    setLocalTextFilter(filterState.searchText || '');
  }, [filterState.searchText]);

  useEffect(() => {
    if (pageNumber !== 1) setPageNumber(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterState]);

  // A filter change on a later page triggers two loads in a row (new filter on
  // the old page, then page 1). Only the most recent request may update state.
  const latestRequestId = useRef(0);

  const loadData = useCallback(async () => {
    const requestId = ++latestRequestId.current;
    const isStale = () => requestId !== latestRequestId.current;
    try {
      setLoading(true);
      setError(null);

      let result: PaginatedResult<Client | ExtendedPet>;
      const filterParams = getPaginationParams();
      const openIdParam =
        pendingOpenId.current !== null ? { openId: pendingOpenId.current } : {};

      if (beingType === 'clients') {
        result = await clientsAPI.getAllPaginated({
          pageNumber,
          pageSize,
          ...filterParams,
          ...openIdParam,
        });
      } else {
        const petsResult = (await petsAPI.getAllPaginated({
          pageNumber,
          pageSize,
          ...filterParams,
          ...openIdParam,
        })) as PaginatedResult<ExtendedPet>;
        if (isStale()) return;

        const clientIds = petsResult.data
          .map((p) => p.client?.id)
          .filter((id) => !!id) as number[];

        if (clientIds.length > 0) {
          const allPetsForClients = await petsAPI.getAllPaginated({
            pageNumber: 1,
            pageSize: 5000,
            clientIds,
          });
          if (isStale()) return;
          setAllPetsForSiblingCalc(allPetsForClients.data);
          result = petsResult;
        } else {
          setAllPetsForSiblingCalc([]);
          result = petsResult;
        }
      }

      if (isStale()) return;
      setItems(result.data);
      setTotalCount(result.totalCount);
      setTotalPages(result.totalPages);
      setHasNextPage(result.hasNextPage);
      setHasPreviousPage(result.hasPreviousPage);

      if (pendingOpenId.current !== null) {
        const targetId = pendingOpenId.current;
        pendingOpenId.current = null;

        const found = result.data.some((item) => item.id === targetId);
        if (!found) {
          // Stale link or filtered out - silently drop the open marker.
          setOpenItemId(null);
        } else {
          shouldScrollToOpen.current = true;
          if (result.pageNumber !== pageNumber) {
            // Server auto-jumped to a different page; adopt without re-fetching.
            skipNextLoad.current = true;
            setPageNumber(result.pageNumber);
          }
        }
      }
    } catch (err) {
      if (isStale()) return;
      const errorMessage = getApiErrorMessage(
        err,
        beingType === 'clients' ? 'LOAD_CLIENTS' : 'LOAD_PETS'
      );
      setError(errorMessage);
      handleError(err, { context: `loading ${beingType}`, showToast: false });
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [
    beingType,
    pageNumber,
    pageSize,
    getPaginationParams,
    handleError,
    setOpenItemId,
  ]);

  useEffect(() => {
    const loadPetTypes = async () => {
      try {
        const types = await referenceDataAPI.getPetTypes();
        setPetTypes(types);
      } catch {
        // pet types are non-critical, silently skip
      }
    };
    if (beingType === 'pets') loadPetTypes();
  }, [beingType]);

  useEffect(() => {
    if (skipNextLoad.current) {
      skipNextLoad.current = false;
      return;
    }
    loadData();
  }, [loadData]);

  // After an auto-jump load lands, scroll the open row into view. Runs only
  // once per flag flip, so user-driven open clicks during the session do not
  // re-scroll.
  useEffect(() => {
    if (
      !loading &&
      shouldScrollToOpen.current &&
      openItemId !== null &&
      items.some((item) => item.id === openItemId)
    ) {
      shouldScrollToOpen.current = false;
      const idPrefix = beingType === 'clients' ? 'client' : 'pet';
      const el = document.getElementById(`${idPrefix}-row-${openItemId}`);
      el?.scrollIntoView({ behavior: 'instant', block: 'center' });
    }
  }, [loading, items, openItemId, beingType]);

  const handlePageChange = (newPage: number) => setPageNumber(newPage);
  const handleFilterButtonClick = () => setFilterDrawerOpen(true);

  const handleApplyFilters = (drawerFilters: Partial<FilterState>) => {
    commitFilters({ ...filterState, ...drawerFilters });
    setFilterDrawerOpen(false);
  };

  const handleClearFilters = () => {
    setLocalTextFilter('');
    clearFilters();
  };

  const handleClearDrawerFilters = () => {
    commitFilters({
      letterFilter: filterState.letterFilter,
      searchText: filterState.searchText,
      sortOrder: 'asc',
    });
  };

  // Letter and search text are mirrored into the URL the same way as the open
  // marker, so Back from a client or pet page restores them.
  const handleLetterFilterChange = (letter: string | null) => {
    setFilter('letterFilter', letter || undefined);
    replaceUrlParam('letterFilter', letter || undefined);
  };

  const handleTextFilterChange = useCallback(
    (text: string) => {
      setLocalTextFilter(text);
      setFilter('searchText', text || undefined);
      replaceUrlParam('searchText', text || undefined);
    },
    [setFilter, replaceUrlParam]
  );

  const handleEdit = (id: number) => navigate(`/${beingType}/${id}/edit`);
  const handleView = (id: number) => navigate(`/${beingType}/${id}`);
  const handleDelete = (_id: number) => {};

  const handleNewBooking = (id: number) => {
    if (beingType === 'clients') {
      navigate(`/clients/${id}/newbooking`);
    } else {
      const pet = items.find((item) => item.id === id) as ExtendedPet;
      if (pet?.client?.id) navigate(`/clients/${pet.client.id}/newbooking`);
    }
  };

  const handleViewBookings = (id: number) => {
    if (beingType === 'clients') {
      navigate(`/clients/${id}/bookings`);
    } else {
      const pet = items.find((item) => item.id === id) as ExtendedPet;
      if (pet?.client?.id) navigate(`/clients/${pet.client.id}/bookings`);
    }
  };

  const handleViewAdmin = (id: number) => {
    if (beingType === 'clients') {
      navigate(`/clients/${id}/admin`);
    } else {
      const pet = items.find((item) => item.id === id) as ExtendedPet;
      if (pet?.client?.id) navigate(`/clients/${pet.client.id}/admin`);
    }
  };

  const handleViewMedical = (_id: number) => {};
  const handleOwnerClick = (clientId: number) =>
    navigate(`/clients/${clientId}`);
  const handlePetClick = (petId: number) => navigate(`/pets/${petId}`);
  const handleNewClick = () => navigate(`/${beingType}/new`);

  const filterDescription = useMemo(() => {
    const parts: string[] = [];
    if (filterState.letterFilter) parts.push(`"${filterState.letterFilter}"`);
    if (localTextFilter) parts.push(`"${localTextFilter}"`);
    if (filterState.hasPets === true) parts.push('has pets');
    if (filterState.hasPets === false) parts.push('no pets');
    if (filterState.hasBookings === true) parts.push('has bookings');
    if (filterState.hasBookings === false) parts.push('no bookings');
    if (filterState.hasOverdueInvoices === true) parts.push('overdue invoices');
    if (filterState.hasOverdueInvoices === false)
      parts.push('no overdue invoices');
    if (filterState.hasOutstandingBalance === true)
      parts.push('outstanding balance');
    if (filterState.hasOutstandingBalance === false)
      parts.push('fully paid up');
    if (filterState.stillOwned === true) parts.push('still owned');
    if (filterState.stillOwned === false) parts.push('no longer owned');
    if (filterState.petTypeIds?.length) {
      const names = filterState.petTypeIds
        .map((id) => petTypes.find((pt) => pt.id === id)?.type)
        .filter(Boolean)
        .join(', ');
      if (names) parts.push(names);
    }
    return parts.join(', ');
  }, [
    filterState.letterFilter,
    filterState.hasPets,
    filterState.hasBookings,
    filterState.hasOverdueInvoices,
    filterState.hasOutstandingBalance,
    filterState.stillOwned,
    filterState.petTypeIds,
    localTextFilter,
    petTypes,
  ]);

  const drawerFilterDescription = useMemo(() => {
    const parts: string[] = [];
    if (filterState.hasPets === true) parts.push('Has pets');
    if (filterState.hasPets === false) parts.push('No pets');
    if (filterState.hasBookings === true) parts.push('Has bookings');
    if (filterState.hasBookings === false) parts.push('No bookings');
    if (filterState.hasOverdueInvoices === true) parts.push('Overdue invoices');
    if (filterState.hasOverdueInvoices === false)
      parts.push('No overdue invoices');
    if (filterState.hasOutstandingBalance === true)
      parts.push('Outstanding balance');
    if (filterState.hasOutstandingBalance === false)
      parts.push('Fully paid up');
    if (filterState.stillOwned === true) parts.push('Still owned');
    if (filterState.stillOwned === false) parts.push('No longer owned');
    if (filterState.petTypeIds?.length) {
      const names = filterState.petTypeIds
        .map((id) => petTypes.find((pt) => pt.id === id)?.type)
        .filter(Boolean)
        .join(', ');
      if (names) parts.push(names);
    }
    return parts.join(', ');
  }, [
    filterState.hasPets,
    filterState.hasBookings,
    filterState.hasOverdueInvoices,
    filterState.hasOutstandingBalance,
    filterState.stillOwned,
    filterState.petTypeIds,
    petTypes,
  ]);

  const pageTitle = beingType === 'clients' ? 'Clients' : 'Pets';

  if (loading) {
    return (
      <PageContainer>
        <VisuallyHiddenHeading title={pageTitle} />
        <LoadingSpinner message={`Loading ${beingType}...`} size={80} />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <VisuallyHiddenHeading title={pageTitle} />
        <PageError message={error} onRetry={loadData} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <VisuallyHiddenHeading title={pageTitle} />
      <AlphabeticalFilter
        activeFilter={filterState.letterFilter || null}
        onFilterChange={handleLetterFilterChange}
        textFilter={localTextFilter}
        onTextFilterChange={handleTextFilterChange}
        itemCount={totalCount}
        contextType={beingType}
        showMobileSearch={showMobileSearch}
        setShowMobileSearch={setShowMobileSearch}
        showMobileAlphabetMenu={showMobileAlphabetMenu}
        setShowMobileAlphabetMenu={setShowMobileAlphabetMenu}
        onMenuToggle={setIsFilterMenuOpen}
        activeFilterCount={activeFilterCount}
        onFilterButtonClick={handleFilterButtonClick}
        pageNumber={pageNumber}
        pageSize={pageSize}
        totalCount={totalCount}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        onPageChange={handlePageChange}
        filterDescription={drawerFilterDescription}
        onClearAllFilters={handleClearDrawerFilters}
        onResetAll={handleClearFilters}
      />

      <ListContainer>
        {beingType === 'clients' ? (
          <ClientsList
            items={items as Client[]}
            type="clients"
            onEdit={handleEdit}
            onView={handleView}
            onDelete={handleDelete}
            onNewBooking={handleNewBooking}
            onViewBookings={handleViewBookings}
            onViewAdmin={handleViewAdmin}
            onViewMedical={handleViewMedical}
            onOwnerClick={handleOwnerClick}
            onPetClick={handlePetClick}
            openItemId={openItemId}
            onOpenChange={setOpenItemId}
          />
        ) : (
          <PetsList
            items={items as ExtendedPet[]}
            allPetsForSiblingCalc={allPetsForSiblingCalc}
            type="pets"
            onEdit={handleEdit}
            onView={handleView}
            onDelete={handleDelete}
            onNewBooking={handleNewBooking}
            onViewBookings={handleViewBookings}
            onViewAdmin={handleViewAdmin}
            onViewMedical={handleViewMedical}
            onOwnerClick={handleOwnerClick}
            onPetClick={handlePetClick}
            openItemId={openItemId}
            onOpenChange={setOpenItemId}
          />
        )}
      </ListContainer>

      <FilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        beingType={beingType as 'clients' | 'pets'}
        filterState={filterState}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        petTypes={petTypes}
      />

      {beingType === 'clients' &&
        !isFilterMenuOpen &&
        !showMobileAlphabetMenu && (
          <FloatingAccessButton
            icon={<AddIcon />}
            hoverIcon={<PersonAddIcon />}
            onClick={handleNewClick}
            aria-label="Add new client"
            iconScale={1.5}
            backgroundColor={theme.palette.secondary.main}
            hoverBackgroundColor={theme.palette.secondary.dark}
            bottom={totalPages > 1 ? '7rem' : '2rem'}
            right="5%"
            sx={{
              border: '2px solid #fff',
              '@media (max-width: 600px)': {
                bottom: totalPages > 1 ? '8.5rem' : '1rem',
                right: '1rem',
              },
              '@media (min-width: 601px)': {
                width: '75px',
                height: '75px',
                minWidth: '75px',
                minHeight: '75px',
              },
            }}
          />
        )}
      <PageNumberText current={pageNumber} total={totalPages} />
      {totalPages > 1 ? (
        <BottomRow>
          {totalPages >= 1 && (
            <PaginationBottom
              pageNumber={pageNumber}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              beingType={beingType}
              activeFilter={filterState.letterFilter || null}
              textFilter={localTextFilter}
              hasNextPage={hasNextPage}
              hasPreviousPage={hasPreviousPage}
              onPageChange={handlePageChange}
            />
          )}
        </BottomRow>
      ) : (
        <Box
          sx={{
            height: '5rem',
            textAlign: 'center',
            borderTop: '2px solid',
            borderTopColor: theme.palette.divider,
            boxShadow: colors.shadows.powerInset,
          }}
        >
          <OnePageBottomText>
            {totalCount} {beingType} found
            {filterDescription ? ` for ${filterDescription}` : ''}
          </OnePageBottomText>
        </Box>
      )}
    </PageContainer>
  );
};

export default BeingsListPage;
